import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import BankSearchModal from '../../components/BankSearchModal';
import { getBank, resolveBankByIdentifier, setBankPassword } from '../../api/banks';
import './ChangeTerminalPasswordPage.css';

const firstErrorMessage = (err) => {
  try {
    const parsed = JSON.parse(err.message);
    const first = Object.values(parsed)[0];
    return Array.isArray(first) ? first[0] : first;
  } catch (_) {
    return 'Sauvegarde impossible.';
  }
};

// Portail (2026-09-26) : la MEME page sert la company (configuration par defaut
// ci-dessous) et le superviseur (pages/supervisor/supervisorCentralPortal.jsx :
// SupervisorLayout, API limitee a SES banks, champ Banque superviseur).
const COMPANY_PORTAL = {
  Layout,
  homePath: '/dashboard',
  centralPath: '/central',
  SearchModal: BankSearchModal,
  resolveBank: resolveBankByIdentifier,
  getBank,
  setPassword: setBankPassword,
};

const ChangeTerminalPasswordPage = ({ portal = COMPANY_PORTAL }) => {
  const [usernameInput, setUsernameInput] = useState('');
  const [lookupError, setLookupError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [bank, setBank] = useState(null);

  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  const resolveTyped = async () => {
    const u = usernameInput.trim();
    if (!u) { setLookupError(''); setBank(null); return; }
    const found = await portal.resolveBank(u);
    if (found) {
      setBank(found);
      setLookupError('');
    } else {
      setBank(null);
      setLookupError('Banque introuvable');
    }
  };

  const handleSelectFromModal = async (id, name, username) => {
    setShowModal(false);
    setUsernameInput(username || '');
    setSaved(false);
    try {
      const full = await portal.getBank(id);
      setBank(full);
      setLookupError('');
    } catch (_) {
      setBank(null);
      setLookupError('Banque introuvable');
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!bank) {
      setError('Choisis d\'abord une banque.');
      return;
    }
    if (password.length < 6) {
      setError('Le mot de passe doit faire au moins 6 caractères.');
      return;
    }
    if (password !== passwordConfirm) {
      setError('Les mots de passe ne correspondent pas.');
      return;
    }
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      await portal.setPassword(bank.id, { password, password_confirm: passwordConfirm });
      setSaved(true);
      setPassword('');
      setPasswordConfirm('');
    } catch (err) {
      setError(firstErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <portal.Layout>
      <p className="ctp-breadcrumb">
        Ruta : <Link to={portal.homePath} className="bc-link">Menu</Link>
        {' >> '}
        <Link to={portal.centralPath} className="bc-link">Central</Link>
        {' >> '}
        <span className="bc-current">Changer Terminal Password</span>
      </p>

      <div className="ctp-card">
        <div className="ctp-card-header">
          <span className="ctp-card-title">Changer Terminal Password</span>
        </div>
        <div className="ctp-card-body">
          <div className="ctp-row">
            <label className="ctp-label">Username</label>
            <input
              className="ctp-input"
              type="text"
              value={usernameInput}
              onChange={(e) => { setUsernameInput(e.target.value); setLookupError(''); }}
              onFocus={(e) => e.target.select()}
              onBlur={resolveTyped}
            />
            <span className="ctp-icon-btn" onClick={() => setShowModal(true)}>🔍</span>
          </div>
          {lookupError && <p className="ctp-error">{lookupError}</p>}

          {showModal && (
            <portal.SearchModal onSelect={handleSelectFromModal} onClose={() => setShowModal(false)} />
          )}

          {bank && (
            <div className="ctp-info">
              {bank.seq != null && (
                <div className="ctp-info-row">
                  <span className="ctp-info-label">N° banque</span>
                  <span>
                    <strong>{bank.seq}</strong>
                    {' '}&nbsp;—&nbsp; Ref fiche : <strong>{String(bank.seq).padStart(2, '0')}-{String(bank.seq).padStart(2, '0')}</strong>
                  </span>
                </div>
              )}
              <div className="ctp-info-row"><span className="ctp-info-label">Nom</span><span>{bank.name}</span></div>
              <div className="ctp-info-row"><span className="ctp-info-label">Username</span><span>{bank.username}</span></div>
              <div className="ctp-info-row"><span className="ctp-info-label">ID POS</span><span>{bank.seq ?? '—'}</span></div>
              <div className="ctp-info-row"><span className="ctp-info-label">Groupe</span><span>{bank.group_name || '—'}</span></div>
              <div className="ctp-info-row"><span className="ctp-info-label">Superviseur</span><span>{(bank.supervisor_names || []).join(', ') || '—'}</span></div>
              <div className="ctp-info-row"><span className="ctp-info-label">Adresse</span><span>{bank.address || '—'}</span></div>
              <div className="ctp-info-row"><span className="ctp-info-label">Téléphone</span><span>{bank.phone || '—'}</span></div>
            </div>
          )}

          {bank && (
            <>
              <hr className="ctp-divider" />

              <form onSubmit={submit}>
                <div className="ctp-row">
                  <label className="ctp-label">Mot de passe</label>
                  <input
                    className="ctp-input"
                    type="password"
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setSaved(false); }}
                  />
                </div>
                <div className="ctp-row">
                  <label className="ctp-label">Confirmer</label>
                  <input
                    className="ctp-input"
                    type="password"
                    value={passwordConfirm}
                    onChange={(e) => { setPasswordConfirm(e.target.value); setSaved(false); }}
                  />
                </div>

                {error && <p className="ctp-error">{error}</p>}
                {saved && <p className="ctp-ok">Mot de passe mis à jour.</p>}

                <button type="submit" className="ctp-save-btn" disabled={saving}>
                  {saving ? 'Sauvegarde...' : 'Sauvegarder'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </portal.Layout>
  );
};

export default ChangeTerminalPasswordPage;
